import { toURLSearchParams } from '@wix/sdk-runtime/rest-modules';
import { resolveUrl } from '@wix/sdk-runtime/rest-modules';
import { ResolveUrlOpts } from '@wix/sdk-runtime/rest-modules';
import { RequestOptionsFactory } from '@wix/sdk-types';

function resolveWixCloudPermissionsV1DataPermissionsServiceUrl(
  opts: Omit<ResolveUrlOpts, 'domainToMappings'>
) {
  const domainToMappings = {
    'manage._base_domain_': [
      {
        srcPath: '/_api/cloud-data/v1/permissions',
        destPath: '/v1/permissions',
      },
    ],
    'editor._base_domain_': [
      {
        srcPath: '/_api/cloud-data/v1/permissions',
        destPath: '/v1/permissions',
      },
    ],
    'blocks._base_domain_': [
      {
        srcPath: '/_api/cloud-data/v1/permissions',
        destPath: '/v1/permissions',
      },
    ],
    'create.editorx': [
      {
        srcPath: '/_api/cloud-data/v1/permissions',
        destPath: '/v1/permissions',
      },
    ],
    'www.wixapis.com': [
      {
        srcPath: '/wix-data/v1/permissions',
        destPath: '/v1/permissions',
      },
    ],
    '*.dev.wix-code.com': [
      {
        srcPath: '/_api/data/v1/permissions',
        destPath: '/v1/permissions',
      },
    ],
    _: [
      {
        srcPath: '/_api/data/v1/permissions',
        destPath: '/v1/permissions',
      },
    ],
  };

  return resolveUrl(Object.assign(opts, { domainToMappings }));
}

const PACKAGE_NAME = '@wix/auto_sdk_data_permissions';

/** Retrieves the current permissions for the specified data collection. */
export function getPermissions(payload: object): RequestOptionsFactory<any> {
  function __getPermissions({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.data.v1.data_permissions',
      method: 'GET' as any,
      methodFqn:
        'wix.cloud.permissions.v1.DataPermissionsService.GetPermissions',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixCloudPermissionsV1DataPermissionsServiceUrl({
        protoPath: '/v1/permissions',
        data: payload,
        host,
      }),
      params: toURLSearchParams(payload),
    };

    return metadata;
  }

  return __getPermissions;
}

/**
 * Updates permissions for the specified data collection.
 *
 * > **Note:** To update special permissions, call [Update Special Permissions](https://dev.wix.com/docs/rest/business-solutions/cms/data-permissions/update-special-permissions).
 */
export function updatePermissions(payload: object): RequestOptionsFactory<any> {
  function __updatePermissions({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.data.v1.data_permissions',
      method: 'POST' as any,
      methodFqn:
        'wix.cloud.permissions.v1.DataPermissionsService.UpdatePermissions',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixCloudPermissionsV1DataPermissionsServiceUrl({
        protoPath: '/v1/permissions',
        data: payload,
        host,
      }),
      data: payload,
    };

    return metadata;
  }

  return __updatePermissions;
}

/**
 * Adds special permissions to a specific user or role for the specified data collection.
 *
 * Special permissions allow specific users or roles to perform actions that [collection-level permissions](https://dev.wix.com/docs/api-reference/business-solutions/cms/data-permissions/introduction#collection-level-permissions) restrict.
 */
export function addSpecialPermissions(
  payload: object
): RequestOptionsFactory<any> {
  function __addSpecialPermissions({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.data.v1.data_permissions',
      method: 'POST' as any,
      methodFqn:
        'wix.cloud.permissions.v1.DataPermissionsService.AddSpecialPermissions',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixCloudPermissionsV1DataPermissionsServiceUrl({
        protoPath: '/v1/permissions/special',
        data: payload,
        host,
      }),
      data: payload,
    };

    return metadata;
  }

  return __addSpecialPermissions;
}

/**
 * Updates the special permissions for the specified user or role.
 *
 * > **Note:** Special permissions that aren't explicitly included in the request are set to `UNSPECIFIED` and replaced by [collection-level permissions](https://dev.wix.com/docs/api-reference/business-solutions/cms/data-permissions/introduction#collection-level-permissions).
 */
export function updateSpecialPermissions(
  payload: object
): RequestOptionsFactory<any> {
  function __updateSpecialPermissions({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.data.v1.data_permissions',
      method: 'POST' as any,
      methodFqn:
        'wix.cloud.permissions.v1.DataPermissionsService.UpdateSpecialPermissions',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixCloudPermissionsV1DataPermissionsServiceUrl({
        protoPath: '/v1/permissions/special/{specialPermissions.id}',
        data: payload,
        host,
      }),
      data: payload,
    };

    return metadata;
  }

  return __updateSpecialPermissions;
}

/**
 * Removes special permissions from a specific user or role.
 *
 * When you remove special permissions, [collection-level permissions](https://dev.wix.com/docs/api-reference/business-solutions/cms/data-permissions/introduction#collection-level-permissions) apply to the user or role for all actions.
 */
export function removeSpecialPermissions(
  payload: object
): RequestOptionsFactory<any> {
  function __removeSpecialPermissions({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.data.v1.data_permissions',
      method: 'DELETE' as any,
      methodFqn:
        'wix.cloud.permissions.v1.DataPermissionsService.RemoveSpecialPermissions',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixCloudPermissionsV1DataPermissionsServiceUrl({
        protoPath: '/v1/permissions/special/{specialPermissionsId}',
        data: payload,
        host,
      }),
      params: toURLSearchParams(payload),
    };

    return metadata;
  }

  return __removeSpecialPermissions;
}

/** Retrieves the current user's permissions for the specified data collection. */
export function getMyPermissions(payload: object): RequestOptionsFactory<any> {
  function __getMyPermissions({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.data.v1.data_permissions',
      method: 'GET' as any,
      methodFqn:
        'wix.cloud.permissions.v1.DataPermissionsService.GetMyPermissions',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixCloudPermissionsV1DataPermissionsServiceUrl({
        protoPath: '/v1/permissions/current',
        data: payload,
        host,
      }),
      params: toURLSearchParams(payload),
    };

    return metadata;
  }

  return __getMyPermissions;
}
